import { useNavigate } from 'react-router-dom';
import Filas from './Filas';
import CollapsibleSection from './CollapsibleSection';
import {
  canShowOntologyGraph,
  canShowOntologyGraphOption,
  isOntologyGraphFeatureVisible,
} from '@/config/ontologyGraph';
import SharedButton from '@/global/Button/Button';
import { useState } from 'react';
import { userAppContext } from '@/context/Context';
import { UserContext } from '@/types';

interface DetailDisgenetProps {
  values: any;
  vocabulary: any[];
  /** Id de la enfermedad (desde selectedInfoItem o payload del detalle). */
  diseaseId?: string | number;
}

export default function DetailDisgenet({ values, vocabulary, diseaseId }: DetailDisgenetProps) {
  const navigate = useNavigate();
  const id =
    diseaseId != null
      ? String(diseaseId)
      : values?.diseaseID != null
        ? String(values.diseaseID)
        : values?.id != null
          ? String(values.id)
          : '';
  const showRow = canShowOntologyGraphOption(id);
  const showButton = canShowOntologyGraph(id);
  const { groups } = userAppContext() as unknown as UserContext;
  const isAcademicPlan = groups?.includes('ROLE_ACADEMIC');

  const [showAll, setShowAll] = useState(false);

  const handleViewOntologyGraph = () => {
    if (!id) return;
    navigate(`/ontology-graph?disease=${encodeURIComponent(id)}&ontoID=id`);
  };
  const infoCardValues = (vocabularyValue: string) => {
    let value = '';
    const diseaseClasses = values.diseaseClasses;

    for (let i = 0; i < diseaseClasses.length; i++) {
      if (diseaseClasses[i].vocabulary === vocabularyValue)
        if (value !== '') value += '; ' + diseaseClasses[i].name;
        else value += diseaseClasses[i].name;
    }
    return value;
  };

  const getMSH = infoCardValues('MSH');
  const getSemantic = infoCardValues('UMLS_ST');
  const getPhenotypes = infoCardValues('HPO');
  const getOntology = infoCardValues('DO');
  const diseaseCui = values?.diseaseUMLSCUI ?? '';
  const externalLink = diseaseCui ? `https://www.ncbi.nlm.nih.gov/medgen/${diseaseCui}` : '';

  return (
    <div className="space-y-0">
      <CollapsibleSection title="General Info" defaultOpen>
        <table className="table table-sm mb-0">
          <tbody>
            <tr>
              <td className="text-xs">
                <strong>Name:</strong>
              </td>
              <td className="text-xs">
                <a
                  href={`/search?view=DISEASES&idents=${diseaseCui}&source=${isAcademicPlan ? 'CURATED' : 'ALL'}&tab=GDA`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-semibold"
                  title={`View ${diseaseCui} in DISGENET`}
                >
                  {values.name} <i className="fa fa-external-link" aria-hidden="true"></i>
                </a>
              </td>
            </tr>
            <tr>
              <td className="text-xs">
                <strong>Type:</strong>
              </td>
              <td className="text-xs">{values.type}</td>
            </tr>
            <tr>
              <td className="text-xs">
                <strong>MeSH Class:</strong>
              </td>
              <td className="text-xs"> {getMSH}</td>
            </tr>
            <tr>
              <td className="text-xs">
                <strong>Semantic Type:</strong>
              </td>
              <td className="text-xs">{getSemantic}</td>
            </tr>
            <tr>
              <td className="text-xs">
                <strong>Phenotypes:</strong>
              </td>
              <td className="text-xs">{getPhenotypes}</td>
            </tr>
            <tr>
              <td className="text-xs">
                <strong>Disease Ontology:</strong>
              </td>
              <td className="text-xs">{getOntology}</td>
            </tr>
            {externalLink && (
              <tr>
                <td className="text-xs">
                  <strong>MedGen:</strong>
                </td>
                <td className="text-xs">
                  <a
                    href={externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tab-links"
                    title={`View ${diseaseCui} in NCBI MedGen`}
                  >
                    {diseaseCui} <i className="fa fa-external-link" aria-hidden="true"></i>
                  </a>
                </td>
              </tr>
            )}
            {showRow && isOntologyGraphFeatureVisible && (
              <tr>
                <td className="text-xs">
                  <strong>Ontology graph:</strong>
                </td>
                <td className="text-xs">
                  {showButton ? (
                    <SharedButton
                      variant="secondaryOutline"
                      isActive
                      size="xs"
                      onClick={handleViewOntologyGraph}
                    >
                      View ontology graph
                    </SharedButton>
                  ) : (
                    <span className="text-muted small">(available when endpoint is ready)</span>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </CollapsibleSection>

      <CollapsibleSection title="Vocabulary" defaultOpen>
        <table className="table table-sm mb-0">
          <tbody>
            {vocabulary.map((resultado: any, index: number) => {
              return (
                <Filas key={`${resultado}${index}`} resultado={resultado} lista={values.diseaseCodes}></Filas>
              );
            })}
          </tbody>
        </table>
      </CollapsibleSection>

      <CollapsibleSection title="Synonyms" defaultOpen>
        <table className="table table-sm mb-0">
          <tbody>
            {(showAll ? values.synonyms : values.synonyms.slice(0, 10)).map((synonym: any, index: number) => {
              return (
                <tr key={`DetailDisgenet${index}`}>
                  <td className="col-md-6 text-xs font-semibold">{synonym.name}</td>
                </tr>
              );
            })}

            {values.synonyms?.length > 10 && !showAll && (
              <tr>
                <td>
                  <button
                    style={{ color: 'rgb(107, 151, 208)' }}
                    className="flex justify-start text-xs font-semibold"
                    onClick={() => setShowAll(true)}
                  >
                    +{values.synonyms.length - 10} synonyms
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </CollapsibleSection>
    </div>
  );
}
